// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
// Licensed under the Apache License, Version 2.0 (https://www.apache.org/licenses/LICENSE-2.0).

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import '@cloudscape-design/global-styles/index.css';
import './styles.css';
import { applyTxdotTheme } from './txdotTheme';
import { initColorMode } from './lib/colorMode';
import { App } from './App';
import { configureApi } from './api/client';
import { configureAuth, handleSessionExpired } from './auth/auth';
import { loadRuntimeConfig } from './config';

applyTxdotTheme();
// Before the first render, so a dark-mode user never sees a light flash.
initColorMode();

const root = createRoot(document.getElementById('root')!);

loadRuntimeConfig()
  .then((config) => {
    configureAuth(config);
    // Still 401 after a forced refresh: keep the draft and sign in again on the same route (requirement 1.4).
    configureApi({ onUnauthorized: () => void handleSessionExpired() });
    root.render(
      <StrictMode>
        <App />
      </StrictMode>,
    );
  })
  .catch((err: unknown) => {
    root.render(<p role="alert">The app could not start: {String((err as Error).message)}</p>);
  });
